<template>
  <v-sheet width="100%" outlined class="ma-2 pa-2">
    <v-row>
      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 section-card">
          <v-card-title class="section-title">
            Kinase Association
          </v-card-title>

          <v-btn
              v-show="!kinaseFiltersVisible"
              small
              dark
              elevation="0"
              class="mx-3 my-2"
              style="text-transform: none"
              @click="kinaseFiltersVisible = true"
          >
            Kinase Filters
            <v-icon small>mdi-chevron-double-right</v-icon>
          </v-btn>

          <v-sheet v-if="kinaseFiltersVisible" outlined rounded class="ma-3">
            <v-sheet class="d-flex justify-space-between align-center ma-3">
              <span class="font-weight-bold">Kinase Filters</span>

              <v-sheet>
                <v-btn
                    small
                    color="secondary"
                    class="mr-2"
                    style="text-transform: none"
                    @click="resetKinaseFilters"
                >
                  Reset
                </v-btn>

                <v-btn
                    small
                    dark
                    elevation="0"
                    style="text-transform: none"
                    @click="kinaseFiltersVisible = false"
                >
                  Hide
                  <v-icon small>mdi-chevron-double-left</v-icon>
                </v-btn>
              </v-sheet>
            </v-sheet>

            <v-sheet class="ma-4">
              <v-combobox
                  v-model="selectedKinaseCancerTypes"
                  :items="kinaseCancerTypes"
                  label="Cancer Type"
                  multiple
                  clearable
                  dense
                  outlined
              />

              <v-combobox
                  v-model="selectedKinases"
                  :items="kinaseNames"
                  label="Kinase"
                  multiple
                  clearable
                  dense
                  outlined
              />
            </v-sheet>
          </v-sheet>

          <v-sheet class="pa-1 chart-box" id="kinase-volcano-container" />

          <v-sheet class="d-flex align-center justify-space-between mt-4">
            <v-card-title class="pa-0">Kinase association table</v-card-title>

            <v-btn
                color="primary"
                dense
                small
                style="text-transform: none"
                @click="downloadKinaseTSV"
            >
              Download TSV
              <v-icon small>mdi-download-box-outline</v-icon>
            </v-btn>
          </v-sheet>

          <v-data-table
              dense
              fixed-header
              height="420px"
              class="mt-3"
              :headers="kinaseHeaders"
              :items="filteredKinaseData"
              :items-per-page="10"
              :footer-props="{
              'items-per-page-options': [10, 20, 50, 100],
              'show-current-page': true
            }"
          >
            <template v-slot:item.kinase_correlation="{ item }">
              {{ formatNumber(item.kinase_correlation) }}
            </template>

            <template v-slot:item.kinase_p_value="{ item }">
              {{ formatNumber(item.kinase_p_value) }}
            </template>

            <template v-slot:item.kinase_norm_p_value="{ item }">
              {{ formatNumber(item.kinase_norm_p_value) }}
            </template>

            <template v-slot:item.cis_correlation="{ item }">
              {{ formatNumber(item.cis_correlation) }}
            </template>

            <template v-slot:item.cis_p_value="{ item }">
              {{ formatNumber(item.cis_p_value) }}
            </template>

            <template v-slot:item.cis_norm_p_value="{ item }">
              {{ formatNumber(item.cis_norm_p_value) }}
            </template>

            <template v-slot:item.association_action="{ item }">
              <v-icon small dense color="primary" @click="openAssociation(item)">
                mdi-eye-arrow-right-outline
              </v-icon>
            </template>

            <template v-slot:item.cis_action="{ item }">
              <v-icon small color="primary" @click="openCisAssociation(item)">
                mdi-eye-arrow-right-outline
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 section-card">
          <v-card-title class="section-title">
            Phosphatase Association
          </v-card-title>

          <v-btn
              v-show="!phosphataseFiltersVisible"
              small
              dark
              elevation="0"
              class="mx-3 my-2"
              style="text-transform: none"
              @click="phosphataseFiltersVisible = true"
          >
            Phosphatase Filters
            <v-icon small>mdi-chevron-double-right</v-icon>
          </v-btn>

          <v-sheet v-if="phosphataseFiltersVisible" outlined rounded class="ma-3">
            <v-sheet class="d-flex justify-space-between align-center ma-3">
              <span class="font-weight-bold">Phosphatase Filters</span>

              <v-sheet>
                <v-btn
                    small
                    color="secondary"
                    class="mr-2"
                    style="text-transform: none"
                    @click="resetPhosphataseFilters"
                >
                  Reset
                </v-btn>

                <v-btn
                    small
                    dark
                    elevation="0"
                    style="text-transform: none"
                    @click="phosphataseFiltersVisible = false"
                >
                  Hide
                  <v-icon small>mdi-chevron-double-left</v-icon>
                </v-btn>
              </v-sheet>
            </v-sheet>

            <v-sheet class="ma-4">
              <v-combobox
                  v-model="selectedPhosphataseCancerTypes"
                  :items="phosphataseCancerTypes"
                  label="Cancer Type"
                  multiple
                  clearable
                  dense
                  outlined
              />

              <v-combobox
                  v-model="selectedPhosphatases"
                  :items="phosphataseNames"
                  label="Phosphatase"
                  multiple
                  clearable
                  dense
                  outlined
              />
            </v-sheet>
          </v-sheet>

          <v-sheet class="pa-1 chart-box" id="phosphatase-volcano-container" />

          <v-sheet class="d-flex align-center justify-space-between mt-4">
            <v-card-title class="pa-0">Phosphatase association table</v-card-title>

            <v-btn
                color="primary"
                dense
                small
                style="text-transform: none"
                @click="downloadPhosphataseTSV"
            >
              Download TSV
              <v-icon small>mdi-download-box-outline</v-icon>
            </v-btn>
          </v-sheet>

          <v-data-table
              dense
              fixed-header
              height="420px"
              class="mt-3"
              :headers="phosphataseHeaders"
              :items="filteredPhosphataseData"
              :items-per-page="10"
              :footer-props="{
              'items-per-page-options': [10, 20, 50, 100],
              'show-current-page': true
            }"
          >
            <template v-slot:item.phosphatase_correlation="{ item }">
              {{ formatNumber(item.phosphatase_correlation) }}
            </template>

            <template v-slot:item.phosphatase_p_value="{ item }">
              {{ formatNumber(item.phosphatase_p_value) }}
            </template>

            <template v-slot:item.phosphatase_norm_p_value="{ item }">
              {{ formatNumber(item.phosphatase_norm_p_value) }}
            </template>

            <template v-slot:item.cis_correlation="{ item }">
              {{ formatNumber(item.cis_correlation) }}
            </template>

            <template v-slot:item.cis_p_value="{ item }">
              {{ formatNumber(item.cis_p_value) }}
            </template>

            <template v-slot:item.cis_norm_p_value="{ item }">
              {{ formatNumber(item.cis_norm_p_value) }}
            </template>

            <template v-slot:item.association_action="{ item }">
              <v-icon small color="primary" @click="openAssociation(item)">
                mdi-eye-arrow-right-outline
              </v-icon>
            </template>

            <template v-slot:item.cis_action="{ item }">
              <v-icon small color="primary" @click="openCisAssociation(item)">
                mdi-eye-arrow-right-outline
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showDetailCard" max-width="620">
      <v-card v-if="selectedPoint">
        <v-card-title>
          {{ selectedPointTypeLabel }} Association Detail
          <v-spacer />
          <v-btn icon @click="closeDetailCard">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div><b>Internal ID:</b> {{ selectedPoint.internal_id }}</div>
          <div><b>Cancer Type:</b> {{ selectedPoint.cancer_type }}</div>
          <div><b>Substrate:</b> {{ selectedPoint.substrate }}</div>
          <div><b>Protein:</b> {{ selectedPoint.harm_protein }}</div>
          <div><b>Site:</b> {{ selectedPoint.harm_site }}</div>
          <div><b>{{ selectedPointTypeLabel }}:</b> {{ selectedPoint.gene_symbol }}</div>

          <div class="mt-3">
            <b>
              {{ selectedPointTypeLabel }} Association
              <v-btn
                  x-small
                  color="primary"
                  class="ma-1"
                  @click="openAssociation(selectedPoint)"
              >
                View
              </v-btn>
            </b>

            <div>Correlation: {{ formatNumber(selectedPoint.correlation) }}</div>
            <div>p-value: {{ formatNumber(selectedPoint.p_value) }}</div>
            <div>-log10(p-value): {{ formatNumber(selectedPoint.norm_p_value) }}</div>
          </div>

          <div class="mt-3">
            <b>
              Protein cis Association
              <v-btn
                  x-small
                  color="primary"
                  class="ma-1"
                  @click="openCisAssociation(selectedPoint)"
              >
                View
              </v-btn>
            </b>

            <div>cis correlation: {{ formatNumber(selectedPoint.cis_correlation) }}</div>
            <div>cis p-value: {{ formatNumber(selectedPoint.cis_p_value) }}</div>
            <div>cis -log10(p-value): {{ formatNumber(selectedPoint.cis_norm_p_value) }}</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import Highcharts from "highcharts";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";

Exporting(Highcharts);
OfflineExporting(Highcharts);

export default {
  name: "KinasePhosphatase",

  props: {
    ksaData: {
      type: Object,
      required: true,
      default: () => ({
        kinase: [],
        phosphatase: [],
      }),
    },
  },

  data() {
    return {
      kinaseFiltersVisible: false,
      phosphataseFiltersVisible: false,

      selectedKinaseCancerTypes: [],
      selectedPhosphataseCancerTypes: [],

      selectedKinases: [],
      selectedPhosphatases: [],

      kinaseChart: null,
      phosphataseChart: null,

      selectedPoint: null,
      selectedPointType: null,
      showDetailCard: false,

      colorMap: {
        BRCA: "#fd8cd5",
        CCRCC: "#ed7711",
        COAD: "#0728e4",
        GBM: "#62666b",
        HNSCC: "#89263b",
        LSCC: "#cb4763",
        LUAD: "#d3d3d3",
        OV: "#107d9d",
        PDAC: "#b80ec4",
        UCEC: "#f04688",
      },

      kinaseHeaders: [
        { text: "Cancer Type", value: "cancer_type" },
        { text: "Kinase", value: "kinase" },
        { text: "Correlation", value: "kinase_correlation" },
        { text: "p-value", value: "kinase_p_value" },
        { text: "-log10(p)", value: "kinase_norm_p_value" },
        { text: "View", value: "association_action", sortable: false },
        { text: "cis Correlation", value: "cis_correlation" },
        { text: "cis p-value", value: "cis_p_value" },
        { text: "cis -log10(p)", value: "cis_norm_p_value" },
        { text: "cis Association", value: "cis_action", sortable: false },
      ],

      phosphataseHeaders: [
        { text: "Cancer Type", value: "cancer_type" },
        { text: "Phosphatase", value: "phosphatase" },
        { text: "Correlation", value: "phosphatase_correlation" },
        { text: "p-value", value: "phosphatase_p_value" },
        { text: "-log10(p)", value: "phosphatase_norm_p_value" },
        { text: "View", value: "association_action", sortable: false },
        { text: "cis Correlation", value: "cis_correlation" },
        { text: "cis p-value", value: "cis_p_value" },
        { text: "cis -log10(p)", value: "cis_norm_p_value" },
        { text: "cis Association", value: "cis_action", sortable: false },
      ],
    };
  },

  computed: {
    rawKinaseData() {
      return this.normalizeRows(this.ksaData.kinase || [], "kinase");
    },

    rawPhosphataseData() {
      return this.normalizeRows(this.ksaData.phosphatase || [], "phosphatase");
    },

    filteredKinaseData() {
      return this.rawKinaseData.filter(item => {
        const matchCancer =
            this.selectedKinaseCancerTypes.length === 0 ||
            this.selectedKinaseCancerTypes.includes(item.cancer_type);

        const matchKinase =
            this.selectedKinases.length === 0 ||
            this.selectedKinases.includes(item.kinase);

        return matchCancer && matchKinase;
      });
    },

    filteredPhosphataseData() {
      return this.rawPhosphataseData.filter(item => {
        const matchCancer =
            this.selectedPhosphataseCancerTypes.length === 0 ||
            this.selectedPhosphataseCancerTypes.includes(item.cancer_type);

        const matchPhosphatase =
            this.selectedPhosphatases.length === 0 ||
            this.selectedPhosphatases.includes(item.phosphatase);

        return matchCancer && matchPhosphatase;
      });
    },

    kinaseCancerTypes() {
      return [...new Set(this.rawKinaseData.map(item => item.cancer_type).filter(Boolean))].sort();
    },

    phosphataseCancerTypes() {
      return [...new Set(this.rawPhosphataseData.map(item => item.cancer_type).filter(Boolean))].sort();
    },

    kinaseNames() {
      return [...new Set(this.rawKinaseData.map(item => item.kinase).filter(Boolean))].sort();
    },

    phosphataseNames() {
      return [...new Set(this.rawPhosphataseData.map(item => item.phosphatase).filter(Boolean))].sort();
    },

    selectedPointTypeLabel() {
      if (this.selectedPointType === "kinase") return "Kinase";
      if (this.selectedPointType === "phosphatase") return "Phosphatase";
      return "";
    },
  },

  watch: {
    ksaData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.renderCharts();
        });
      },
    },

    filteredKinaseData() {
      this.$nextTick(() => {
        this.renderKinaseChart();
      });
    },

    filteredPhosphataseData() {
      this.$nextTick(() => {
        this.renderPhosphataseChart();
      });
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.renderCharts();
    });
  },

  beforeDestroy() {
    [this.kinaseChart, this.phosphataseChart].forEach(chart => {
      if (chart && !chart.destroyed) {
        chart.destroy();
      }
    });

    this.kinaseChart = null;
    this.phosphataseChart = null;
  },

  methods: {
    normalizeRows(rows, type) {
      return rows.map(row => {
        const isKinase = type === "kinase";
        const parsed = this.parseInternalId(row.internal_id);

        const associationGene = isKinase ? row.kinase : row.phosphatase;
        const correlation = Number(
            isKinase ? row.kinase_correlation : row.phosphatase_correlation
        );
        const pValue = Number(
            isKinase ? row.kinase_p_value : row.phosphatase_p_value
        );
        const normPValue = Math.abs(Number(
            isKinase ? row.kinase_norm_p_value : row.phosphatase_norm_p_value
        ));

        return {
          ...row,
          type,
          substrate: parsed.substrate,
          harm_site: parsed.harm_site,
          harm_protein: parsed.harm_protein,
          gene_symbol: associationGene,
          correlation,
          p_value: pValue,
          norm_p_value: normPValue,
          cis_correlation: Number(row.cis_correlation),
          cis_p_value: Number(row.cis_p_value),
          cis_norm_p_value: Math.abs(Number(row.cis_norm_p_value)),
        };
      });
    },

    parseInternalId(internalId) {
      if (!internalId || typeof internalId !== "string") {
        return {
          substrate: "",
          harm_site: "",
          harm_protein: "",
        };
      }

      const parts = internalId.split("_");

      return {
        substrate: parts[0] || "",
        harm_site: parts[1] || "",
        harm_protein: parts[2] || "",
      };
    },

    resetKinaseFilters() {
      this.selectedKinaseCancerTypes = [];
      this.selectedKinases = [];
    },

    resetPhosphataseFilters() {
      this.selectedPhosphataseCancerTypes = [];
      this.selectedPhosphatases = [];
    },

    renderCharts() {
      this.renderKinaseChart();
      this.renderPhosphataseChart();
    },

    renderKinaseChart() {
      this.kinaseChart = this.renderVolcanoChart({
        chart: this.kinaseChart,
        containerId: "kinase-volcano-container",
        title: "Kinase Association",
        rows: this.filteredKinaseData,
        type: "kinase",
        xTitle: "Kinase-PTM correlation coefficient",
      });
    },

    renderPhosphataseChart() {
      this.phosphataseChart = this.renderVolcanoChart({
        chart: this.phosphataseChart,
        containerId: "phosphatase-volcano-container",
        title: "Phosphatase Association",
        rows: this.filteredPhosphataseData,
        type: "phosphatase",
        xTitle: "Phosphatase-PTM correlation coefficient",
      });
    },

    prepareVolcanoSeries(rows) {
      const groupedData = {};

      rows.forEach(item => {
        const x = Number(item.correlation);
        const y = Math.abs(Number(item.norm_p_value));

        if (!isFinite(x) || !isFinite(y)) return;

        const cancerType = item.cancer_type || "Unknown";

        if (!groupedData[cancerType]) {
          groupedData[cancerType] = [];
        }

        groupedData[cancerType].push({
          x,
          y,
          marker: {
            symbol: "circle",
            radius: y > 3 ? 4 : 2,
          },
          ...item,
        });
      });

      return Object.keys(groupedData).map(cancerType => ({
        name: cancerType,
        data: groupedData[cancerType],
        color: this.colorMap[cancerType] || "#666666",
      }));
    },

    renderVolcanoChart({ chart, containerId, title, rows, type, xTitle }) {
      const vm = this;
      const seriesData = this.prepareVolcanoSeries(rows);

      if (chart && !chart.destroyed) {
        chart.destroy();
      }

      const newChart = Highcharts.chart(containerId, {
        chart: {
          type: "scatter",
          zoomType: "xy",
        },

        title: {
          text: title,
          style: {
            fontSize: "1.1rem",
            fontWeight: "bold",
          },
        },

        xAxis: {
          title: {
            text: xTitle,
          },
          gridLineWidth: 1,
          crosshair: true,
          plotLines: [
            { color: "black", width: 1, value: 0, zIndex: 1 },
            { color: "gray", width: 1, value: 0.3, dashStyle: "Dash", zIndex: 1 },
            { color: "gray", width: 1, value: -0.3, dashStyle: "Dash", zIndex: 1 },
          ],
        },

        yAxis: {
          title: {
            text: "-log10(p-value)",
          },
          gridLineWidth: 1,
          crosshair: true,
          min: 0,
          plotLines: [
            { color: "black", width: 1, value: 0 },
            { color: "gray", width: 1, value: 3, dashStyle: "Dash", zIndex: 1 },
          ],
        },

        legend: {
          enabled: true,
        },

        plotOptions: {
          scatter: {
            marker: {
              radius: 4,
              symbol: "circle",
            },
          },
          series: {
            cursor: "pointer",
            point: {
              events: {
                click: function () {
                  vm.openDetailCard(this, type);
                },
              },
            },
          },
        },

        tooltip: {
          hideDelay: 50,
          formatter() {
            return `
              <b>${this.point.gene_symbol || ""}</b><br/>
              Cancer Type: ${this.point.cancer_type || ""}<br/>
              Correlation: ${vm.formatNumber(this.point.x)}<br/>
              -log10(p-value): ${vm.formatNumber(this.point.y)}
            `;
          },
        },

        series: seriesData,

        exporting: {
          enabled: true,
          sourceWidth: 600,
          sourceHeight: 600,
          scale: 8,
          buttons: {
            contextButton: {
              menuItems: ["downloadPNG", "downloadSVG"],
            },
          },
        },

        credits: {
          enabled: false,
        },
      });

      if (seriesData.length === 0) {
        newChart.renderer
            .text("No data", newChart.chartWidth / 2, newChart.chartHeight / 2)
            .attr({ align: "center" })
            .css({ color: "#999999", fontSize: "14px" })
            .add();
      }

      return newChart;
    },

    openDetailCard(point, type) {
      this.selectedPoint = point;
      this.selectedPointType = type;
      this.showDetailCard = true;
    },

    closeDetailCard() {
      this.showDetailCard = false;
      this.selectedPoint = null;
      this.selectedPointType = null;
    },

    openAssociation(item) {
      if (!item) return;

      const url =
          "https://kb.linkedomics.org/plot/phospho" +
          `?protein=${encodeURIComponent(item.harm_protein || "")}` +
          `&site=${encodeURIComponent(item.harm_site || "")}` +
          `&cohort=${encodeURIComponent(item.cancer_type || "")}` +
          `&genesymbol=${encodeURIComponent(item.gene_symbol || "")}` +
          "&datatype=protein";

      window.open(url, "_blank");
    },

    openCisAssociation(item) {
      if (!item) return;

      const url =
          "https://kb.linkedomics.org/plot/phospho" +
          `?protein=${encodeURIComponent(item.harm_protein || "")}` +
          `&site=${encodeURIComponent(item.harm_site || "")}` +
          `&cohort=${encodeURIComponent(item.cancer_type || "")}` +
          `&genesymbol=${encodeURIComponent(item.substrate || "")}` +
          "&datatype=protein";

      window.open(url, "_blank");
    },

    downloadKinaseTSV() {
      this.downloadTSV(
          this.filteredKinaseData,
          this.kinaseHeaders,
          "ptmatlas_site_kinase_association.tsv"
      );
    },

    downloadPhosphataseTSV() {
      this.downloadTSV(
          this.filteredPhosphataseData,
          this.phosphataseHeaders,
          "ptmatlas_site_phosphatase_association.tsv"
      );
    },

    downloadTSV(data, headers, filename) {
      const content = this.convertToTSV(data, headers);
      const blob = new Blob([content], {
        type: "text/tab-separated-values;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.setAttribute("href", url);
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },

    convertToTSV(data, headers) {
      const exportHeaders = headers.filter(header => header.sortable !== false);

      const headerLine = exportHeaders
          .map(header => this.escapeTSVValue(header.text))
          .join("\t");

      const rows = data.map(item => {
        return exportHeaders
            .map(header => this.escapeTSVValue(item[header.value]))
            .join("\t");
      });

      return [headerLine, ...rows].join("\n");
    },

    escapeTSVValue(value) {
      if (value === null || value === undefined) return "";

      return String(value)
          .replace(/\t/g, " ")
          .replace(/\n/g, " ")
          .replace(/\r/g, " ");
    },

    formatNumber(value) {
      const num = Number(value);

      if (!isFinite(num)) return "NA";

      if (Math.abs(num) >= 1000 || Math.abs(num) < 0.001) {
        return num.toExponential(1);
      }

      return num.toFixed(2);
    },
  },
};
</script>

<style scoped>
.section-card {
  min-height: 100%;
  border: 2px solid #d0d0d0 !important;
  border-radius: 10px;
}

.section-title {
  font-weight: 700;
  font-size: 1.05rem;
  border-bottom: 1px solid #d0d0d0;
  margin-bottom: 10px;
  padding-bottom: 10px;
}

.chart-box {
  width: 100%;
  min-height: 430px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

#kinase-volcano-container,
#phosphatase-volcano-container {
  width: 100%;
  min-height: 430px;
}

::v-deep .v-data-table {
  font-size: 12px;
}
</style>